// Brigade has no server-side profile signal (unlike DC via orderProfile) - it's
// identified by an optional "brigade" path segment in the app URL, e.g.
// /<projectId>/<roomId>/brigade/<token>. That segment is only present on the
// very first load(s); later in-session URL rewrites (room-switch pushState in
// UpperRailComponent, which rewrites to just /projectId/roomId) drop it, so
// the result is latched into localStorage to survive those. The app never
// switches to a *different* project within the same tab without a full page
// load, so the flag is only ever set, never cleared - a fresh load for a
// genuinely different project always re-derives it from that load's own URL.
const BRIGADE_FLAG_KEY = 'is_brigade_profile';

const hasBrigadeSegment = (segments: string[]): boolean => {
    return segments.some(seg => seg.toLowerCase() === 'brigade');
};

// Call once per fresh page load (DrawingWizard mount) with the raw, unmodified
// URL segments. Latches a positive result so detection survives later
// in-session URL rewrites that drop the segment.
export const detectAndLatchBrigadeProfile = (segments: string[]): boolean => {
    const isBrigade = hasBrigadeSegment(segments);
    if (isBrigade) {
        try {
            localStorage.setItem(BRIGADE_FLAG_KEY, 'true');
        } catch (e) {
            // localStorage unavailable (private mode, etc.) - URL-based checks still work this load
        }
    }
    return isBrigade;
};

// Removes a "brigade" path segment (if present) so the remaining segments can
// be parsed positionally (projectId/roomId/token) the same way as before.
export const stripBrigadeSegment = (segments: string[]): string[] => {
    const idx = segments.findIndex(seg => seg.toLowerCase() === 'brigade');
    return idx === -1 ? segments : [...segments.slice(0, idx), ...segments.slice(idx + 1)];
};

export const isBrigadeProfile = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }
    if (hasBrigadeSegment(window.location.href.split('/'))) {
        return true;
    }
    try {
        return localStorage.getItem(BRIGADE_FLAG_KEY) === 'true';
    } catch (e) {
        return false;
    }
};

export const getSpacecraftDrawingUrl = (): string => {
    return isBrigadeProfile()
        ? process.env.REACT_APP_SPACECRAFT_DRAWING_BRIGADE_URL
        : process.env.REACT_APP_SPACECRAFT_DRAWING_URL;
};

export const getSpacecraftUrl = (): string => {
    return isBrigadeProfile()
        ? process.env.REACT_APP_SPACECRAFT_BRIGADE_URL
        : process.env.REACT_APP_SPACECRAFT_URL;
};
